import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { iniciales } from '../lib/iniciales';
import { JobsProvider } from '../jobs/JobsContext';
import { NAV_GRUPOS, NAV_ITEMS, type NavItem } from './navItems';
import logoColor from '../assets/ambec-logo-color.svg';
import { Ayuda } from '../components/Ayuda';

/**
 * Shell del área privada (rol user) — mismo sistema aprobado del admin:
 * lienzo casi blanco, MARCO redondeado cerrado en los 4 lados, sidebar tintada
 * (única superficie con color) con widget de usuario, navegación agrupada
 * (Operación / Agentes) y logout al pie. El scroll ocurre dentro del marco.
 */

const iconClass = 'h-4 w-4 shrink-0';

/** Título (y ayuda ⓘ opcional) de la página actual a partir de la ruta más específica que coincide. */
function tituloActual(pathname: string): Pick<NavItem, 'titulo' | 'ayuda'> {
  if (pathname.startsWith('/perfil')) return { titulo: 'Mi Perfil' };
  const match = NAV_ITEMS.filter((i) => pathname.startsWith(i.to)).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];
  return { titulo: match?.titulo ?? 'AMBEC', ayuda: match?.ayuda };
}

function SidebarContenido({ onNavegar }: { onNavegar?: () => void }) {
  const { profile, signOut, esAdmin, reelegirModo } = useAuth();
  const [cerrando, setCerrando] = useState(false);

  async function onCerrarSesion() {
    setCerrando(true);
    try {
      await signOut();
    } finally {
      setCerrando(false);
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Marca */}
      <div className="flex items-center px-4 pt-6">
        <img src={logoColor} alt="AMBEC" className="h-5 w-auto" />
      </div>

      {/* Widget de usuario (card blanca sobre la sidebar tintada) → Mi Perfil */}
      <Link to="/perfil" title="Mi Perfil y mi actividad" className="mx-3 mt-6 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 transition-colors hover:border-celeste">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-azul-profundo/10 text-[10px] font-semibold text-azul-profundo"
        >
          {iniciales(profile?.full_name, profile?.email)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium leading-tight text-azul-noche">
            {profile?.full_name || profile?.email}
          </p>
          <p className="truncate text-[10px] font-normal leading-tight text-gris">
            {profile?.email}
          </p>
        </div>
      </Link>

      {/* Navegación agrupada (Operación / Agentes) */}
      <nav aria-label="Navegación principal" className="mt-4 flex-1 overflow-y-auto px-3">
        {NAV_GRUPOS.map((grupo) => (
          <div key={grupo.titulo} className="mb-4">
            <p className="px-2.5 text-[9px] font-medium uppercase tracking-[0.12em] text-gris/60">
              {grupo.titulo}
            </p>
            <ul className="mt-1.5 flex flex-col gap-0.5">
              {grupo.items.filter((item) => !item.oculto).map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={onNavegar}
                    className={({ isActive }) =>
                      [
                        'group flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo',
                        isActive
                          ? 'border border-slate-200 bg-white font-medium text-azul-profundo shadow-[0_1px_2px_rgba(1,15,39,0.04)]'
                          : 'border border-transparent font-normal text-texto/75 hover:bg-white/70 hover:text-azul-noche',
                      ].join(' ')
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          aria-hidden="true"
                          className={
                            isActive
                              ? 'text-azul-profundo'
                              : 'text-gris group-hover:text-azul-noche'
                          }
                        >
                          {item.icon}
                        </span>
                        <span className="flex-1">{item.label}</span>
                        {item.enDesarrollo && (
                          <span className="rounded bg-white px-1.5 py-px text-[9px] font-medium text-gris ring-1 ring-inset ring-slate-200">
                            Pronto
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Cerrar sesión al pie */}
      <div className="px-3 pb-4">
        {esAdmin && (
          <button
            type="button"
            onClick={reelegirModo}
            title="Estás en modo Usuario (vista de cliente). Cambiar a Administrador."
            className="mb-1 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] font-normal text-texto/75 transition-colors duration-150 ease-out hover:bg-white/70 hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo"
          >
            <svg viewBox="0 0 24 24" className={`${iconClass} text-azul-profundo`} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 3h5v5"/><path d="M8 21H3v-5"/><path d="M21 3 3 21"/></svg>
            Cambiar de vista
          </button>
        )}
        <button
          type="button"
          onClick={onCerrarSesion}
          disabled={cerrando}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] font-normal text-texto/75 transition-colors duration-150 ease-out hover:bg-white/70 hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
        >
          <svg
            viewBox="0 0 24 24"
            className={`${iconClass} text-red-500`}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="m16 17 5-5-5-5M21 12H9" />
          </svg>
          {cerrando ? 'Cerrando…' : 'Cerrar sesión'}
        </button>
        <p className="mt-2.5 px-2.5 text-[10px] font-light text-gris/70">
          AMBEC · Plataforma de Agentes
        </p>
        <p className="mt-0.5 px-2.5 text-[10px] font-light tabular-nums text-gris/50">
          release {__RELEASE__}
        </p>
      </div>
    </div>
  );
}

export default function PrivateLayout() {
  const location = useLocation();
  const [drawerAbierto, setDrawerAbierto] = useState(false);

  useEffect(() => {
    setDrawerAbierto(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerAbierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerAbierto(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerAbierto]);

  const { titulo, ayuda } = tituloActual(location.pathname);

  return (
    // Lienzo casi blanco de página: se ve en los 4 costados alrededor del marco.
    <JobsProvider>
    <div className="app-privada min-h-dvh bg-[#FAFBFC] px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-5">
      {/* Container global de la app: máx. 1280px centrado (regla de UI) */}
      <div className="mx-auto w-full max-w-[1440px]">
        {/* MARCO de la app: cerrado en los 4 lados; scroll interno. */}
        <div className="flex h-[calc(100dvh-1.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:h-[calc(100dvh-2rem)] lg:h-[calc(100dvh-2.5rem)]">
          {/* Sidebar tintada (desktop) */}
          <aside className="hidden h-full w-56 shrink-0 border-r border-slate-200/70 bg-[#E8EDF2] lg:block">
            <SidebarContenido />
          </aside>

          {/* Drawer móvil */}
          {drawerAbierto && (
            <div className="fixed inset-0 z-40 lg:hidden">
              <div
                className="absolute inset-0 bg-azul-noche/40"
                onClick={() => setDrawerAbierto(false)}
                aria-hidden="true"
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Menú de navegación"
                className="absolute inset-y-0 left-0 w-64 border-r border-slate-200 bg-[#E8EDF2] shadow-xl"
              >
                <div className="absolute right-3 top-3">
                  <button
                    type="button"
                    onClick={() => setDrawerAbierto(false)}
                    aria-label="Cerrar menú"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-white hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="m6 6 12 12M18 6 6 18" />
                    </svg>
                  </button>
                </div>
                <SidebarContenido onNavegar={() => setDrawerAbierto(false)} />
              </div>
            </div>
          )}

          {/* Columna principal (scroll interno) */}
          <div className="flex h-full min-w-0 flex-1 flex-col">
            <header className="shrink-0 border-b border-slate-200/70 bg-white lg:hidden">
              <div className="flex h-14 items-center gap-3 px-4">
                <button
                  type="button"
                  onClick={() => setDrawerAbierto(true)}
                  aria-label="Abrir menú"
                  aria-expanded={drawerAbierto}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-slate-100 hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <img src={logoColor} alt="AMBEC" className="h-5 w-auto" />
              </div>
            </header>

            <main className="relative flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-8 lg:py-6">
              {/* Título de página (interino: las páginas aún no traen header propio) */}
              <h1 className="titulo-pagina mb-5 flex items-center">
                <span>{titulo}</span>
                {ayuda && <Ayuda texto={ayuda} />}
              </h1>
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </div>
    </JobsProvider>
  );
}
